/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";
import { EasInputGate } from "../EasInputGate";
import type { TInputEvent, InputInterceptor } from "../types/TInputEvent";

const TAG: string = "EasInputBlockInterceptor";

/**
 * Stop a consumed event dead at the DOM, not merely inside the chain.
 *
 * Returning `true` from an interceptor sets `event.stopped`, which keeps the
 * event away from the rest of the chain and from every `ScreenController` —
 * but the raw DOM event carries on regardless. `NavigationEngine` listens on
 * `document` in the capture phase, and React attaches its own handlers to the
 * root container below it, so without this a blocked click still reaches the
 * `onClick` of whatever is underneath the alert and the viewer navigates away
 * mid-emergency. The same holds for the React `onKeyDown` handlers across the
 * screens.
 *
 * `stopPropagation` rather than `stopImmediatePropagation`: the aim is to
 * keep the event from reaching other *nodes*, and any sibling listener on
 * `document` itself is part of the engine's own pipeline.
 *
 * @param event - The event being consumed
 */
function haltRawEvent(event: TInputEvent): void {
  event.raw.stopPropagation();
  // Suppresses the browser's own default for the key or click — scrolling on
  // arrows and space, context menus, link activation.
  if (event.raw.cancelable) {
    event.raw.preventDefault();
  }
}

/**
 * Drops every input except the EAS dismiss action while an Emergency Alert
 * System alert is on screen.
 *
 * Consuming the event here — rather than letting screens ignore it — is what
 * makes the block total. The interceptor chain halts on the first interceptor
 * that returns true, so a blocked event reaches no other interceptor and no
 * `ScreenController`, which means trickplay, channel change, numeric tune,
 * `INFO`/`GUIDE`/`MENU`, colour buttons and pointer input all stop dead
 * regardless of which screen happens to be active. Consumed events are also
 * halted at the DOM — see {@link haltRawEvent}, without which the block stops
 * at the chain's edge and React's own handlers still fire.
 *
 * ## Registration order matters
 *
 * This must be the **first** interceptor on the chain, and is registered with
 * `InterceptorChain.useFirst()` rather than `use()` to guarantee it. Ordinary
 * registration order is not enough: `use()` appends, and under React the
 * hooks called above the registering effect — the secret-code reset, the
 * version override — and every child screen's `useInactivityOsd` all run
 * first. Anything ahead of this one acts on events the alert is meant to
 * swallow; most visibly the secret reset code, which clears the database and
 * reloads the app, and `exitKey`, which closes the application outright on
 * Tizen.
 *
 * ## The dismiss key is consumed here, not forwarded
 *
 * Every event is consumed while an alert is up, BACK included. BACK is turned
 * into `EasInputGate.requestDismiss()` on its `keydown` and then swallowed,
 * rather than allowed through to the rest of the pipeline.
 *
 * Forwarding it was the original design and it was wrong twice over: BACK
 * reached the active `ScreenController`, which on the playback screen
 * navigates to the previous screen — so dismissing an alert also left
 * playback — and it armed `createLongPressInterceptor`'s hold-to-exit timer,
 * which closes the application on Tizen. Consuming it fixes both at the
 * source, and the long-press timer never arms because it never sees the
 * keydown.
 *
 * @param gate - The gate to consult; defaults to the shared instance
 * @returns The interceptor function to pass to `InterceptorChain.useFirst()`
 */
export function createEasInputBlockInterceptor(
  gate: EasInputGate = EasInputGate.getInstance()
): InputInterceptor {
  return (event: TInputEvent): boolean => {
    if (gate.isActionAllowed()) {
      return false;
    }

    // Only on keydown: a dismiss must fire once per press, not again on the
    // matching keyup.
    if (event.eventType === "keydown" && gate.isDismissRequest(event.action)) {
      Log.i(
        TAG,
        "createEasInputBlockInterceptor(): dismiss key consumed — " +
          "finishing EAS alert"
      );
      haltRawEvent(event);
      gate.requestDismiss();
      return true;
    }

    Log.d(
      TAG,
      `createEasInputBlockInterceptor(): blocked ${event.action} ` +
        `(${event.eventType}) — EAS alert on screen`
    );
    haltRawEvent(event);
    return true;
  };
}
